"use client"

import type React from "react"
import Link from "next/link"
import Image from "next/image"
import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useRouter } from "next/navigation"
import { Search, X, ChevronDown, ChevronRight } from "lucide-react"
import { CHEVRON_DOWN_SYMBOL_ID } from "@/components/brand-kit/icon-sprite"
import { HeaderSavedCompare, openHeaderCompare } from "@/components/chrome/header-saved-compare"
import { useSavedCompare } from "@/components/homepage-2026/saved-compare-store"
import { cn } from "@/lib/utils"
import { COMPANY_LOGO_BLUE } from "@/lib/constants"
import { menuData, simpleLinks } from "@/lib/menu-data"
import { useIndexedChromeSlugs } from "@/components/indexed-chrome-slugs"
import { keepIndexedCollectionLinks } from "@/lib/chrome-link-gate"
import { useIsHydrated } from '@/hooks/use-is-hydrated'

// FRIDGE-137 — one stable id for the mega-menu panel; every nav trigger's
// aria-controls points here (the panel contents swap per trigger, the panel
// element itself is singular). Exported for the keyboard-operability test.
export const MEGA_PANEL_ID = "mega-menu-panel"

// FRIDGE-149 §6 — the search typeahead panel's id. The element carrying it is
// ALWAYS in the document (empty and `hidden` while the panel is closed), so
// the combobox's aria-controls resolves in the SERVER-rendered HTML rather
// than only after hydration.
export const SEARCH_RESULTS_ID = "header-search-results"

// FRIDGE-1508 K1508-3 — whether a focus move (the blur's relatedTarget) takes
// focus out of the header search: the inline form and the typeahead panel. A
// move to nowhere (null) is not a leave; see closeSearchOnFocusLeave below.
export function focusLeavesSearch(next: Node | null, ...search: Array<Node | null | undefined>): boolean {
  if (!next) return false
  return !search.some((el) => el != null && el.contains(next))
}

// FRIDGE-1508 K1508-14 — where Escape in the search puts focus: the inline
// search box while it is on screen (1024px and up). Below that the box lives
// inside the panel Escape is closing, so focus goes to the Search button that
// opened it. See closeSearchOnEscape below.
type HasClientRects = { getClientRects(): { length: number } }
export function escapeFocusTarget<Box extends HasClientRects, Toggle extends HasClientRects>(
  inlineBox: Box | null | undefined,
  searchToggle: Toggle | null | undefined,
): Box | Toggle | null {
  if (inlineBox && inlineBox.getClientRects().length > 0) return inlineBox
  return searchToggle ?? null
}

// FRIDGE-1508 K1508-3 (follow-up) — where Tab goes in the header search at
// 1024px and up. The panel is rendered after the header row, so the
// browser's own order ran from the box to Compare and Saved, which left the
// search and closed the panel: no popular search and no result could be
// reached from the keyboard. Tab now follows the panel as it reads: from the
// form's last control into the panel's first, and from the panel's last
// control on to whatever follows the form, which leaves the search and
// closes the panel as before; Shift+Tab from the panel's first control goes
// back to the form. null means the browser's own move is already right,
// which is every other move, and every move below 1024px, where the inline
// form is not on screen and the panel's own box comes first.
export function searchTabStop<T>(
  from: T,
  shift: boolean,
  formStops: readonly T[],
  panelStops: readonly T[],
  afterForm: T | null,
): T | null {
  if (formStops.length === 0 || panelStops.length === 0) return null
  const lastFormStop = formStops[formStops.length - 1]
  if (!shift && from === lastFormStop) return panelStops[0]
  if (!shift && from === panelStops[panelStops.length - 1]) return afterForm
  if (shift && from === panelStops[0]) return lastFormStop
  return null
}

// The keyboard stops inside `root` in document order: what Tab visits.
const TAB_STOPS = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
function tabStopsIn(root: ParentNode | null | undefined): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(TAB_STOPS)).filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0)
}

export function SiteHeader({
  indexedCollectionSlugs,
}: { indexedCollectionSlugs?: readonly string[] | null } = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const savedCompare = useSavedCompare()
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResponse, setSearchResponse] = useState<{ query: string; results: any[] } | null>(null)
  const searchResults = searchResponse?.query === searchQuery ? searchResponse.results : []
  const [showResults, setShowResults] = useState(false)
  // Keyboard-navigable typeahead: -1 = no active row (Enter submits full search).
  const [activeIndex, setActiveIndex] = useState(-1)
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const mounted = useIsHydrated()
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // FRIDGE-137 — keyboard path into the mega-menu panel. The panel is rendered
  // AFTER the desktop search form in the DOM, and focusing the search input
  // closes the panel, so before this fix Tab could literally never reach a
  // panel link. Focus is now routed INTO the panel on keyboard activation.
  const panelRef = useRef<HTMLDivElement | null>(null)
  const triggerRefs = useRef(new Map<string, HTMLAnchorElement>())
  const focusPanelOnOpenRef = useRef(false)
  // FRIDGE-149 §3 — the mobile nav drawer is a real dialog: opening moves
  // focus to its close button, Tab cycles inside it, Escape closes it, and
  // closing returns focus to the hamburger that opened it.
  const mobileNavRef = useRef<HTMLDivElement | null>(null)
  const mobileCloseRef = useRef<HTMLButtonElement | null>(null)
  const hamburgerRef = useRef<HTMLButtonElement | null>(null)
  const mobileMenuWasOpenRef = useRef(false)
  const router = useRouter()

  const recordSelection = (query: string, count: number, destination: string) => {
    void import('@/lib/search/record-outcome').then(({ recordSearchSelection }) => {
      recordSearchSelection(query, count, destination)
    })
  }

  useEffect(() => {
    if (mobileMenuOpen) {
      mobileMenuWasOpenRef.current = true
      mobileCloseRef.current?.focus()
    } else if (mobileMenuWasOpenRef.current) {
      mobileMenuWasOpenRef.current = false
      hamburgerRef.current?.focus()
    }
  }, [mobileMenuOpen])

  const onMobileNavKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault()
      setMobileMenuOpen(false)
      return
    }
    if (e.key !== "Tab" || !mobileNavRef.current) return
    const focusable = Array.from(
      mobileNavRef.current.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.getClientRects().length > 0)
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    if (e.shiftKey && (active === first || !mobileNavRef.current.contains(active))) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const focusPanelFirstLink = useCallback(() => {
    panelRef.current?.querySelector<HTMLElement>("a[href]")?.focus()
  }, [])

  // Keyboard requirement (owner: everything clickable must always work):
  // Escape closes an open mega-menu panel from anywhere and returns focus to
  // the trigger that owns it (WCAG menu pattern — FRIDGE-137). Enter/Space
  // open it natively (the triggers are real <button>s whose onClick toggles).
  useEffect(() => {
    if (!activeMenu) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveMenu(null)
        triggerRefs.current.get(activeMenu)?.focus()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [activeMenu])

  // FRIDGE-137 — after a keyboard activation opens the panel (flag set in the
  // trigger's onKeyDown, never on hover), move focus to its first link so Tab
  // then walks every category link inside the panel.
  useEffect(() => {
    if (activeMenu && focusPanelOnOpenRef.current) {
      focusPanelOnOpenRef.current = false
      focusPanelFirstLink()
    }
  }, [activeMenu, focusPanelFirstLink])

  // Click-outside for search is handled by an invisible backdrop overlay (see below in JSX)
  // This is the same pattern the mobile menu uses — more reliable than document event listeners

  useEffect(() => {
    const controller = new AbortController()
    const searchProducts = async () => {
      if (searchQuery.length < 2) {
        setSearchResponse(null)
        setShowResults(false)
        return
      }
      try {
        const response = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}&limit=8`, {
          signal: controller.signal,
        })
        if (!response.ok) throw new Error('Search request failed')
        const data = await response.json() as { results?: typeof searchResults }
        if (controller.signal.aborted) return
        setSearchResponse({ query: searchQuery, results: data.results || [] })
        setShowResults(true)
        setActiveIndex(-1)
      } catch {
        if (controller.signal.aborted) return
        setSearchResponse(null)
      }
    }
    const debounceTimer = setTimeout(searchProducts, 300)
    return () => { clearTimeout(debounceTimer); controller.abort() }
  }, [searchQuery])

  const closeSearch = useCallback(() => {
    setSearchOpen(false)
    setSearchQuery("")
    setShowResults(false)
    setActiveIndex(-1)
  }, [])

  // FRIDGE-1508 K1508-3 — the typeahead panel opens on the input's focus, and
  // until this it stayed open, with its full-screen backdrop, after Tab left
  // the field: the controls a keyboard visitor tabbed to next sat behind it
  // (893 focused elements on 76 of 78 page types in the DevTools test). Focus
  // moving to anything outside the inline search form and the panel now
  // closes it, as focus leaving the mega menu does (FRIDGE-137 below); focus
  // moving between them, into a result or a popular search, keeps it open.
  // A blur with no relatedTarget is left to the backdrop, Escape and submit:
  // a click on plain text, on the backdrop, or on a result in Safari (which
  // never focuses a link or a button on click) reports none, and closing on
  // it would unmount the result before its click arrives.
  const searchFormRef = useRef<HTMLFormElement | null>(null)
  const searchPanelRef = useRef<HTMLDivElement | null>(null)
  const closeSearchOnFocusLeave = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (focusLeavesSearch(e.relatedTarget as Node | null, searchFormRef.current, searchPanelRef.current)) closeSearch()
    },
    [closeSearch]
  )

  // FRIDGE-1508 K1508-14 — Escape anywhere in the search (the box, its clear
  // button, a popular search, a result, the phone panel's own box) closes the
  // panel and puts focus back on the search box. Until this only the box
  // listened, and it dropped focus to the page; Escape on a popular search or
  // a result did nothing. It listens on the same two surfaces as the
  // focus-leave close. Focus moves first and the panel closes after: focusing
  // the box runs its onFocus, which opens the panel, and the close queued
  // after it is the update that lands.
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const searchToggleRef = useRef<HTMLButtonElement | null>(null)
  const closeSearchOnEscape = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key !== "Escape") return
      escapeFocusTarget(searchInputRef.current, searchToggleRef.current)?.focus()
      closeSearch()
    },
    [closeSearch]
  )

  // K1508-3 follow-up — Tab between the inline form and the panel (see
  // searchTabStop). The stop after the form is looked up only when focus is
  // on the panel's last control, the one move that needs it.
  const routeSearchTab = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Tab") return
    const formStops = tabStopsIn(searchFormRef.current)
    const panelStops = tabStopsIn(searchPanelRef.current)
    let afterForm: HTMLElement | null = null
    if (!e.shiftKey && formStops.length > 0 && e.target === panelStops[panelStops.length - 1]) {
      const all = tabStopsIn(document)
      afterForm = all.slice(all.indexOf(formStops[formStops.length - 1]) + 1).find((el) => !searchPanelRef.current?.contains(el)) ?? null
    }
    const to = searchTabStop(e.target as HTMLElement, e.shiftKey, formStops, panelStops, afterForm)
    if (to) {
      e.preventDefault()
      to.focus()
    }
  }, [])

  // The one key handler on both search surfaces: Escape closes (K1508-14),
  // Tab follows the panel (K1508-3 follow-up).
  const onSearchSurfaceKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      closeSearchOnEscape(e)
      routeSearchTab(e)
    },
    [closeSearchOnEscape, routeSearchTab]
  )

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // An arrow-key-highlighted typeahead row wins over a raw query submit.
    if (activeIndex >= 0 && searchResults[activeIndex]?.url) {
      recordSelection(searchQuery, searchResults.length, searchResults[activeIndex].url)
      router.push(searchResults[activeIndex].url)
      closeSearch()
      return
    }
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery)}`)
      closeSearch()
    }
  }

  // Keyboard navigation over the typeahead results (chrome-upgrade zone 1).
  // Escape is not handled here: it bubbles to closeSearchOnEscape on the form
  // and the panel around both inputs.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showResults || searchResults.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, searchResults.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, -1))
    }
  }

  // Menu hover handlers with delay
  const handleMenuEnter = useCallback((trigger: string) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current)
      menuTimeoutRef.current = null
    }
    setActiveMenu(trigger)
    setSearchOpen(false)
    setSearchQuery("")
    setShowResults(false)
  }, [])

  const handleMenuLeave = useCallback(() => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null)
    }, 150)
  }, [])

  const handlePanelEnter = useCallback(() => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current)
      menuTimeoutRef.current = null
    }
  }, [])

  // seo_disposition gate for the mega-menu collection links (P04 [A-13] /
  // plan §0.16): hide any /collections/<slug> menu item whose collection is
  // not in the server-confirmed indexed set, and drop sections that become
  // empty. The set arrives from the root layout via IndexedChromeSlugsProvider
  // (getIndexedChromeSlugs()); an explicitly passed prop wins over the
  // context. When no set resolved (null) the menu passes through unchanged
  // (the curated collection links were verified all-indexed at wiring time).
  // Gate logic shared from lib/chrome-link-gate (dependency-free) — NOT from
  // the heavy footer module, which must never enter the header bundle.
  const contextIndexedSlugs = useIndexedChromeSlugs()
  const effectiveIndexedSlugs = indexedCollectionSlugs ?? contextIndexedSlugs
  const visibleMenu = useMemo(() => {
    if (!effectiveIndexedSlugs) return menuData
    const indexed = new Set(effectiveIndexedSlugs)
    return menuData.map((category) => ({
      ...category,
      sections: category.sections
        .map((section) => ({
          ...section,
          items: keepIndexedCollectionLinks(section.items, indexed),
        }))
        .filter((section) => section.items.length > 0),
    }))
  }, [effectiveIndexedSlugs])

  const activeCategory = visibleMenu.find(c => c.trigger === activeMenu)

  return (
    <header className="sticky top-0 z-50 w-full bg-white safe-area-top">
      {/* FRIDGE-138 — skip link, the FIRST focusable element on every page
          that renders the shared header (WCAG 2.4.1 Bypass Blocks). Hidden
          until keyboard focus reaches it (.skip-link in app/globals.css),
          then a visible pill over the header. Targets <main id="main-content">
          on each template. */}
      <a
        href="#main-content"
        className="skip-link"
        // Templates whose <main> carries no id still get a working skip: the
        // handler focuses #main-content when present, else the page's first
        // <main> (tabindex=-1 so a landmark can take focus), so the next Tab
        // continues inside the main content on every template. Without a
        // <main> the plain anchor behaviour is left alone.
        onClick={(e) => {
          const target =
            document.getElementById("main-content") ??
            document.querySelector<HTMLElement>("main")
          if (!target) return
          e.preventDefault()
          if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1")
          target.focus()
        }}
      >
        Skip to main content
      </a>
      {/* Header Bar */}
      <div style={{ borderBottom: '6px solid #ff9951' }}>
        {/* min-w-0 lets the row's children yield instead of forcing width;
            overflow-hidden is the last-resort guard so nothing — above all the
            search input — can ever be painted past the right padding edge at
            ANY viewport width (owner search-bar directive 07/20). */}
        <div className="mx-auto max-w-[1400px] flex h-[72px] sm:h-[86px] items-center justify-between pl-2 pr-2 sm:pl-8 sm:pr-4 xl:pl-10 xl:pr-6 min-w-0 overflow-hidden">
          {/* Logo — min-w-0 + max-w-full so it SHRINKS on narrow viewports
              instead of forcing its intrinsic width past the screen edge
              (390px-viewport overflow fix, 07/20/2026). */}
          {/* FRIDGE-1097 sibling, FRIDGE-1114: measured on production at 390px, the
              logo PAINTS 32.7px tall inside its 52px box — object-contain letterboxes
              it by 9.6px top and bottom because 186px of width only supports 32.7px at
              the mark's 5.68:1 ratio. Height is therefore width-bound, so width is what
              the phone breakpoint gives back here. */}
          {/* FRIDGE-1507 S1: at 1024 the row is full (logo 240 + nav 512 + Saved
              and Compare 111), which squeezed the inline search box to 100px and
              cut its "Search…" placeholder to "Se…" (30px of text room for 56px).
              From 1024 to 1052 the logo gives back up to 28px, 100vw - 812px, so
              the box keeps at least 111px even beside a 17px classic scrollbar;
              from 1052 the cap is the 240px it always was. */}
          <div className="min-w-0 mr-1 min-[1024px]:max-w-[min(240px,calc(100vw_-_812px))] xl:max-w-none">
            {/* FRIDGE-1505: no viewport prefetch of "/". It sits in view on every page, and the
                prefetch applied the homepage's preload hints everywhere (its CSS chunks and seven
                retailer marks, a median 50.8 KB per phone page, never used off the homepage). */}
            <Link href="/" prefetch={false} className="flex items-center" aria-label="Fridge.com Logo">
              <span className="sr-only">Fridge.com Logo</span>
              <Image
                src={COMPANY_LOGO_BLUE}
                alt="Fridge.com Logo"
                width={363}
                height={64}
                priority
                className="h-[52px] sm:h-[60px] xl:h-[66px] w-auto max-w-full object-contain object-left"
              />
            </Link>
          </div>

          {/* Desktop Navigation — only show when all items including search fit.
              FRIDGE-782 — rendered in the SERVER pass (it was gated on
              `mounted`, so every non-JS crawler and AI fetcher saw a header
              with no <nav> and zero category links). CSS decides visibility
              per width; the markup is identical on the server and the first
              client render. */}
          {/* FRIDGE-873 — `flex-1 justify-end` keeps this group hard against the
              Saved/Compare cluster that now sits OUTSIDE it (see below). Before
              that move the cluster was the last child of this container, so
              `justify-between` on the row put the whole group on the right; with
              a third always-visible child the row would otherwise strand the nav
              in the middle at wide viewports. Visually identical at every
              width ≥1024px. */}
          <div className="hidden min-[1024px]:flex flex-1 justify-end items-center gap-1 min-w-0 shrink">
            <nav aria-label="Primary" className="flex items-center gap-0.5 xl:gap-1">
              {visibleMenu.map((category) => (
                // FRIDGE-782 — a REAL link to the category's landing page, so
                // the served HTML carries seven crawlable anchors and a visitor
                // without JavaScript still reaches the category. With
                // JavaScript the click is intercepted below and opens the
                // panel exactly as the previous <button> did.
                <Link
                  key={category.trigger}
                  href={category.href}
                  // no prefetch: the link exists for crawlers and non-JS
                  // visitors; a hydrated click opens the panel, so prefetching
                  // seven category routes on every page would be pure cost
                  prefetch={false}
                  ref={(el) => {
                    if (el) triggerRefs.current.set(category.trigger, el)
                    else triggerRefs.current.delete(category.trigger)
                  }}
                  onMouseEnter={() => handleMenuEnter(category.trigger)}
                  onMouseLeave={handleMenuLeave}
                  // Click ALWAYS opens (owner rule: everything clickable
                  // must always work). Hover can open the menu first; a
                  // toggle here would then close it on the very click that
                  // "opened" it. Close paths: Escape, mouse-leave, another
                  // trigger, focus leaving the panel, or navigating. The
                  // default (navigation) is suppressed so hydrated behaviour
                  // is unchanged from the button it replaced.
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveMenu(category.trigger)
                  }}
                  // FRIDGE-137 — keyboard activation routes focus INTO the
                  // panel. Enter fires the click above (the flag makes the
                  // open-effect move focus); Space and ArrowDown open
                  // explicitly (a link has no native Space activation). If
                  // the panel is already open (hover or a prior press), the
                  // same keys just move focus into it.
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " " && e.key !== "ArrowDown") return
                    if (activeMenu === category.trigger) {
                      e.preventDefault()
                      focusPanelFirstLink()
                      return
                    }
                    focusPanelOnOpenRef.current = true
                    if (e.key === " " || e.key === "ArrowDown") {
                      e.preventDefault()
                      setActiveMenu(category.trigger)
                    }
                  }}
                  aria-haspopup="true"
                  aria-expanded={activeMenu === category.trigger}
                  aria-controls={MEGA_PANEL_ID}
                  className={cn(
                    "h-10 px-1 min-[1150px]:px-1.5 xl:px-2 text-[15px] font-medium rounded-full transition-all duration-200 flex items-center gap-0.5",
                    activeMenu === category.trigger
                      ? "bg-[#ff9951]/15 text-[#ff9951]"
                      : "bg-transparent text-gray-900 hover:bg-[#0469f0]/8"
                  )}
                >
                  {category.trigger}
                  {/* One <use> per trigger against the sprite the layout already
                      mounts, not seven full inline copies of the same glyph —
                      the FRIDGE-745 shape, on the chrome this time: the seven
                      SSR'd triggers (FRIDGE-782) put this icon on every page of
                      the site. */}
                  <svg
                    aria-hidden="true"
                    width={24}
                    height={24}
                    className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      activeMenu === category.trigger && "rotate-180"
                    )}
                  >
                    <use href={`#${CHEVRON_DOWN_SYMBOL_ID}`} />
                  </svg>
                </Link>
              ))}
              {simpleLinks.map((link) => (
                <Link
                  key={link.title}
                  href={link.href}
                  className="h-10 px-1.5 xl:px-2 text-[15px] font-medium text-gray-900 hover:bg-gray-100 rounded-full transition-colors flex items-center"
                >
                  {link.title}
                </Link>
              ))}
            </nav>

            {/* Inline search input (chrome-upgrade zone 1, owner-corrected):
                lives IN the nav row — never wraps to a second row. Fluid width
                (clamp) so it shrinks before anything cuts off; below 1150px it
                collapses to the icon button outside this container and the
                overlay panel carries the input instead. */}
            <form
              ref={searchFormRef}
              onSubmit={handleSearchSubmit}
              role="search"
              // The WIDTH lives on the form (not the input) so the input can be
              // w-full and therefore can NEVER exceed its shrunk container. The
              // form's own width is the responsive target; `shrink` + the 72px
              // min-w floor let it yield at the tightest inline width (1024px)
              // while always staying inside the row's right padding — the
              // input's right edge tracks the form's, guaranteeing the >=8px
              // right-edge gap at every width (owner directive 07/20).
              className="ph-no-capture ml-1 min-[1150px]:ml-1.5 xl:ml-2 hidden min-[1024px]:block w-[130px] min-[1150px]:w-[150px] min-[1280px]:w-[180px] min-w-[72px] shrink"
              aria-label="Search Fridge.com"
            >
              {/* The focus-leave close (K1508-3), the Escape close
                  (K1508-14) and the Tab route into the panel listen here,
                  on a wrapper around everything focusable in the form, not
                  on the search landmark itself: a handler on a landmark or
                  a role-less div is what the jsx-a11y gate counts, and
                  role="presentation" is how a container says it only
                  gathers its children's events. */}
              <div role="presentation" onBlur={closeSearchOnFocusLeave} onKeyDown={onSearchSurfaceKeyDown} className="relative min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  name="q"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); if (!searchOpen) setSearchOpen(true); setActiveMenu(null); }}
                  onFocus={() => { setSearchOpen(true); setActiveMenu(null); }}
                  // K1508-14: Escape keeps focus in the box, so a click on it
                  // (which fires no focus event then) opens the panel again.
                  onClick={() => setSearchOpen(true)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search…"
                  aria-label="Search Fridge.com"
                  // FRIDGE-149 §6 — combobox semantics (aria-expanded is only
                  // valid on a combobox, not a bare textbox). Every one of these
                  // is unconditional: the whole point is that the SERVED HTML
                  // describes a complete combobox. Keying any of them off
                  // `mounted` would drop them from the server pass — which is
                  // what the first cut did — and an attribute that differs
                  // between the server and client renders is a hydration
                  // mismatch React refuses to patch up.
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={searchOpen && showResults}
                  aria-controls={SEARCH_RESULTS_ID}
                  aria-activedescendant={showResults && activeIndex >= 0 ? `header-search-option-${activeIndex}` : undefined}
                  // Fixed, container-relative width (NOT vw): the header row is
                  // capped at max-w-[1400px], so a vw-based width kept growing
                  // past the cap at 1440-1600 and pushed the input off the right
                  // edge. A bounded px width + `shrink` on the form guarantees
                  // the single row always fits with breathing room (overflow fix
                  // 07/20/2026).
                  // w-full: the input fills the form and never exceeds it, so
                  // its right edge always tracks the (padding-safe) form edge.
                  // FRIDGE-1507 S1: pr-8 is the clear button's room, and the
                  // button only renders once there is a query; while the
                  // placeholder shows, 12px is enough and the placeholder gets
                  // the other 20px.
                  className="h-10 w-full min-w-0 pl-9 pr-8 placeholder-shown:pr-3 text-sm [text-overflow:ellipsis] bg-gray-50 border border-gray-200 rounded-full focus:bg-white focus:border-[#ff9951] focus:outline-none focus:ring-2 focus:ring-[#ff9951]/20 transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={closeSearch}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* The right-hand cluster — ALWAYS rendered, at every width.

              FRIDGE-873 / FRIDGE-874 / FRIDGE-800: Saved + Compare used to live
              inside the desktop navigation container above, which is
              `hidden min-[1024px]:flex`. Measured on production 09/05/2026 the
              two buttons were 61x44 px at 1920/1440/1100 and 0x0 at 900 and
              below — so on every phone and every narrow laptop the feature did
              not exist, and ticking Compare gave no visible feedback anywhere.
              They now sit in this cluster, outside that container, beside the
              mobile search and hamburger; the text labels still collapse to
              icon-only under 860px (canvas.css), the count badge does not.

              Deliberately NOT `relative`. The Saved and Compare panels are
              `position: absolute` and must resolve against the sticky <header>
              (the nearest positioned ancestor once nothing in between is
              positioned), so their `right` is measured from the viewport edge
              and their `top: 100%` clears the whole header bar. A `relative`
              here would anchor a 440px panel to a ~130px box hard against the
              right edge and push it off-screen. */}
          <div className="flex items-center shrink-0 gap-0.5">
            {/* Saved + Compare (homepage 2026, owner 09/02/2026): live counts and
                panels backed by localStorage — the same compare key the product
                and collection compare tray reads. */}
            <HeaderSavedCompare />

            {/* Search icon (tap-to-expand overlay pattern) + hamburger. The icon
                shows whenever the inline input is collapsed — all mobile widths
                (below 1024px) — and opens the same absolute overlay panel (zero
                added rows, zero layout shift); the panel carries the input at
                these widths. */}
            <div className="flex items-center shrink-0 min-[1024px]:hidden">
              <button
                onClick={() => {
                  const opening = !searchOpen
                  if (opening) { setActiveMenu(null); setSearchOpen(true) }
                  else { closeSearch() }
                }}
                // K1508-14: Escape in the phone panel returns focus here.
                ref={searchToggleRef}
                className={cn(
                  "inline-flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200",
                  searchOpen ? "bg-[#ff9951]/15 text-[#ff9951]" : "hover:bg-gray-100 text-black"
                )}
                aria-label={searchOpen ? "Close search" : "Search"}
                aria-expanded={searchOpen}
                // FRIDGE-149 §6 — unconditional: the panel element it names is
                // always in the document (see SEARCH_RESULTS_ID below).
                aria-controls={SEARCH_RESULTS_ID}
              >
                {searchOpen ? <X className="h-[22px] w-[22px]" /> : <Search className="h-[22px] w-[22px]" />}
              </button>

              {/* Mobile Hamburger — shown when desktop nav is hidden.
                  h-11 w-11 = 44px, matching the search button immediately above
                  rather than picking a new number. It was `p-2` around a 24px icon
                  — 40×40, under the 44px minimum tap target, and the ONLY control
                  in the header below it (FRIDGE-385). The icon size is unchanged,
                  so nothing looks different; the hit area grows by 2px a side. */}
              <button
                ref={hamburgerRef}
                className="min-[1024px]:hidden inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-gray-100"
                onClick={() => { setMobileMenuOpen(true); closeSearch() }}
                aria-label="Open menu"
                aria-haspopup="dialog"
                aria-expanded={mobileMenuOpen}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Full-Width Mega Menu Panel - Text-only, professional design */}
      {/* FRIDGE-782 — the seven triggers now reach the SERVER markup with
          aria-controls={MEGA_PANEL_ID}, so the element they name must exist in
          the same markup: while the panel is closed an empty, hidden
          placeholder carries the id (the same pattern as SEARCH_RESULTS_ID). */}
      {!(mounted && activeMenu && activeCategory) && <div id={MEGA_PANEL_ID} hidden />}
      {mounted && activeMenu && activeCategory && (
        <div
          id={MEGA_PANEL_ID}
          ref={panelRef}
          className="absolute left-0 right-0 top-full -mt-px max-h-[calc(100dvh-86px)] overflow-y-auto overscroll-contain bg-white border-b border-gray-200 shadow-2xl z-[60] pt-px"
          onMouseEnter={handlePanelEnter}
          onMouseLeave={handleMenuLeave}
          // FRIDGE-137 — Tab off the last panel link (or Shift+Tab off the
          // first, past the triggers) closes the panel and lets focus continue
          // to the next page control. Focus moving BETWEEN panel links, or back
          // to a nav trigger, keeps it open.
          onBlur={(e) => {
            const next = e.relatedTarget as Node | null
            if (next && e.currentTarget.contains(next)) return
            if (
              next &&
              Array.from(triggerRefs.current.values()).some(
                (btn) => btn === next || btn.contains(next)
              )
            )
              return
            setActiveMenu(null)
          }}
        >
          <div className="mx-auto max-w-[1400px] pl-4 pr-3 sm:pl-8 sm:pr-4 xl:pl-10 xl:pr-6 py-8">
            <div className="flex gap-0">
              {/* Sections as columns with dividers */}
              {activeCategory.sections.map((section, sectionIndex) => (
                <div 
                  key={section.label} 
                  className={cn(
                    "min-w-0 flex-1 pr-4 xl:pr-8",
                    sectionIndex > 0 && "border-l border-gray-200 pl-4 xl:pl-8"
                  )}
                >
                  <h3 className="text-sm font-semibold text-[#0469f0] uppercase tracking-wider mb-5">
                    {section.label}
                  </h3>
                  <ul className="space-y-1">
                    {section.items.map((item) => (
                      <li key={item.title}>
                        <Link
                          href={item.href}
                          onClick={() => setActiveMenu(null)}
                          className="group block rounded-lg py-2.5 px-3 -mx-3 transition-colors hover:bg-[#0469f0]/5"
                        >
                          <div className="text-[15px] font-medium text-gray-900 group-hover:text-[#0469f0] transition-colors">
                            {item.title}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5">
                            {item.description}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Featured Block */}
              {activeCategory.featured && (
                <div className="hidden xl:block w-[320px] shrink-0 pl-8 border-l border-gray-200">
                  <Link
                    href={activeCategory.featured.href}
                    onClick={() => setActiveMenu(null)}
                    className="group block rounded-2xl bg-gradient-to-br from-[#0469f0]/5 to-[#ff9951]/5 p-6 transition-all duration-200 hover:from-[#0469f0]/10 hover:to-[#ff9951]/10"
                  >
                    <div className="relative aspect-[16/10] w-full overflow-hidden rounded-xl bg-white mb-4 shadow-sm ring-1 ring-black/5">
                      <Image
                        src={activeCategory.featured.image}
                        alt={activeCategory.featured.title}
                        fill
                        className="object-contain transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">
                      {activeCategory.featured.title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                      {activeCategory.featured.description}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0469f0] group-hover:text-[#004aad] transition-colors">
                      {activeCategory.featured.cta}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search backdrop — click anywhere outside to close */}
      {mounted && searchOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={closeSearch}
        />
      )}

      {/* Search typeahead panel (chrome-upgrade zone 1) — results-only; the
          visible desktop/mobile inputs drive it. Keyboard-navigable via
          handleSearchKeyDown (ArrowUp/Down highlight, Enter opens the row). */}
      {/* FRIDGE-149 §6 — the element carrying SEARCH_RESULTS_ID is ALWAYS
          rendered so both header controls' aria-controls resolve in the served
          HTML; while the panel is closed it is empty and `hidden`, so it costs
          ~90 bytes, paints nothing, and stays out of the accessibility tree.
          Its open/closed state comes from the same expression on the server
          and on the first client render, so hydration cannot diverge here.
          FRIDGE-1508 K1508-17 — that element is the listbox and holds the
          result options only. It used to be the whole open panel, so its
          role="listbox" wrapped the headings, the popular-search buttons, the
          phone search form and a list of list items around the options (axe
          4.14: aria-required-children and aria-required-parent, critical).
          The open panel is now a plain container; the listbox sits under the
          Results heading, one option per result, and is hidden while there
          are none. */}
      {mounted && searchOpen ? (
        // role="presentation": the container has no semantics of its own; it
        // only gathers its children's focus and key events (see the inline
        // form).
        <div
          ref={searchPanelRef}
          role="presentation"
          onBlur={closeSearchOnFocusLeave}
          onKeyDown={onSearchSurfaceKeyDown}
          className="ph-no-capture absolute left-0 right-0 top-full bg-white border-b border-gray-200 shadow-2xl z-[60] overflow-hidden"
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-6 lg:py-8">
            {/* Panel search input — carried HERE (inside the overlay, never a
                new header row) at every width where the inline nav input is
                collapsed: mobile (below 1024px). */}
            <form
              onSubmit={handleSearchSubmit}
              role="search"
              aria-label="Search Fridge.com"
              className="ph-no-capture min-[1024px]:hidden mb-5"
            >
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-gray-500 pointer-events-none" />
                <input
                  name="q"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search fridges, brands, tools…"
                  aria-label="Search Fridge.com"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showResults}
                  aria-controls="header-search-results"
                  aria-activedescendant={showResults && activeIndex >= 0 ? `header-search-option-${activeIndex}` : undefined}
                  autoFocus
                  className="w-full h-11 pl-10 pr-9 text-[15px] bg-gray-50 border border-gray-200 rounded-full focus:bg-white focus:border-[#ff9951] focus:outline-none focus:ring-2 focus:ring-[#ff9951]/20 transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </form>
            {/* Mobile: stacked / Desktop: side-by-side like mega menu */}
            <div className="flex flex-col lg:flex-row">
              {/* Left side — popular searches (quick discovery) */}
              <div className="lg:w-[260px] shrink-0 lg:pr-8 pb-5 lg:pb-0 hidden lg:block">
                <h3 className="text-sm font-semibold text-[#0469f0] uppercase tracking-wider mb-4">
                  Popular searches
                </h3>
                <div className="space-y-0.5">
                  {['French door', 'Samsung', 'Mini fridge', 'Chest freezer', 'Wine cooler'].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => { setSearchQuery(suggestion); setSearchOpen(true); }}
                      className="block text-[15px] text-gray-600 hover:text-[#ff9951] transition-colors py-1.5"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right side — Results */}
              <div className="flex-1 min-w-0 lg:pl-8 lg:border-l border-gray-200">
                <h3 className="text-sm font-semibold text-[#ff9951] uppercase tracking-wider mb-4 truncate">
                  {showResults && searchResults.length > 0 ? `Results for \u201c${searchQuery}\u201d` : 'Results'}
                </h3>
                {/* K1508-17: the options are the listbox's only children (no
                    list or list items between them). The thumbnail's alt is
                    empty because the title beside it names the option; with
                    the title as alt as well, every option's name said the
                    title twice. */}
                <div
                  id={SEARCH_RESULTS_ID}
                  role="listbox"
                  aria-label="Search results"
                  hidden={!(showResults && searchResults.length > 0)}
                  className="space-y-0.5"
                >
                  {/* FRIDGE-1509 (L1509-1): no prefetch of suggestions. Every option
                      was prefetched as it appeared and kept in the router cache, so
                      the page grew about 0.44 MB per search (+43% after 20) and made
                      133 prefetch requests in 10 searches on production, 10/14/2026. */}
                  {showResults && searchResults.map((result, index) => (
                    <Link
                      key={index}
                      href={result.url}
                      prefetch={false}
                      id={`header-search-option-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => { recordSelection(searchQuery, searchResults.length, result.url); closeSearch() }}
                      className={cn(
                        "group flex items-center gap-3 rounded-lg py-2.5 px-3 -mx-3 transition-colors",
                        index === activeIndex ? "bg-[#ff9951]/10" : "hover:bg-[#ff9951]/5"
                      )}
                    >
                      {result.image && (
                        <Image src={result.image} alt="" width={44} height={44} className="w-11 h-11 object-contain shrink-0 rounded-lg bg-gray-50 p-1" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={cn(
                          "text-[15px] font-medium truncate transition-colors",
                          index === activeIndex ? "text-[#ff9951]" : "text-gray-900 group-hover:text-[#ff9951]"
                        )}>
                          {result.title}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5 truncate">
                          {/* K1508-17: the chip's margin parts it from the text on
                              screen; the sr-only space parts it in the option's
                              name ("FIN-0000294 Samsung", not "FIN-0000294Samsung")
                              without moving anything that is seen. */}
                          {result.fin ? <><span className="font-mono text-[11px] font-bold text-[#0454c0] bg-[#f0f6ff] border border-[#cfe1fb] rounded px-1.5 py-px mr-1.5">{result.fin}</span><span className="sr-only">{" "}</span></> : null}
                          {result.sub ? `${result.sub} · ` : ''}{result.type}
                        </div>
                      </div>
                      {typeof result.price === 'number' && result.price > 0 ? (
                        <span className="shrink-0 text-[15px] font-bold text-gray-900">{`$${Math.round(result.price / 100).toLocaleString('en-US')}`}</span>
                      ) : null}
                    </Link>
                  ))}
                </div>
                {/* "See all results" is a link to the search page, not one of
                    the options, so it sits after the listbox; mt-0.5 keeps the
                    2px the list's spacing gave it. */}
                {showResults && searchResults.length > 0 ? (
                  searchQuery.trim().length >= 2 && (
                    <div className="mt-0.5">
                      <Link
                        href={`/search?q=${encodeURIComponent(searchQuery)}`}
                        prefetch={false}
                        onClick={closeSearch}
                        className="block py-2.5 px-3 -mx-3 text-sm font-semibold text-[#0469f0] hover:text-[#004aad] transition-colors"
                      >
                        See all results for &ldquo;{searchQuery}&rdquo; &rarr;
                      </Link>
                    </div>
                  )
                ) : showResults && searchResults.length === 0 && searchQuery.length >= 2 ? (
                  <p className="text-gray-500 text-[15px] py-3">No matches for &ldquo;{searchQuery}&rdquo;. Try a brand, model, or type.</p>
                ) : (
                  <p className="text-gray-600 text-[15px] py-3">Type to search refrigerators, freezers, brands, and tools</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div id={SEARCH_RESULTS_ID} role="listbox" aria-label="Search results" hidden />
      )}

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={() => setMobileMenuOpen(false)} />
          {/* FRIDGE-149 §3 — the drawer is a modal dialog (a <nav> may not
              carry role="dialog", so the dialog is the wrapper and the <nav>
              sits inside it with the same column layout). */}
          <div
            ref={mobileNavRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            onKeyDown={onMobileNavKeyDown}
            className="fixed top-0 left-0 h-screen w-[85%] max-w-[320px] bg-white shadow-2xl z-[9999] flex flex-col animate-slide-in"
          >
          <nav aria-label="Main menu" className="flex flex-col flex-1 min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <Link href="/" aria-label="Fridge.com" onClick={() => setMobileMenuOpen(false)}>
                <span className="sr-only">Fridge.com</span>
                <Image src={COMPANY_LOGO_BLUE} alt="Fridge.com" width={200} height={40} className="h-10 w-auto" />
              </Link>
              <button ref={mobileCloseRef} onClick={() => setMobileMenuOpen(false)} className="p-2.5 rounded hover:bg-gray-100" aria-label="Close menu">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <form className="ph-no-capture" onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) { router.push(`/search?q=${encodeURIComponent(searchQuery)}`); setMobileMenuOpen(false); } }}>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input
                      name="q"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full h-10 pl-10 pr-3 text-sm border border-gray-300 rounded-lg focus:border-[#0469f0] focus:outline-none"
                    />
                  </div>
                  <button type="submit" className="h-10 px-4 bg-[#ff9951] text-[var(--color-brand-action-text)] text-sm font-medium rounded-lg hover:bg-[#ff8533]">
                    Go
                  </button>
                </div>
              </form>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto p-4">
              <ul className="space-y-1">
                {/* FRIDGE-1153: below 640px the header's Compare button is
                    hidden, so the drawer carries it — same live count, same
                    panel. From 640px up the header button is back and this
                    item hides. */}
                <li className="min-[640px]:hidden">
                  <button
                    type="button"
                    onClick={() => { setMobileMenuOpen(false); openHeaderCompare() }}
                    className="w-full flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-100 font-medium"
                  >
                    Compare
                    {savedCompare.compare.length > 0 ? (
                      <span className="inline-flex min-w-[22px] h-[22px] items-center justify-center rounded-full bg-[#ff9951] px-1.5 text-xs font-extrabold text-[#101828]" aria-label={`${savedCompare.compare.length} in your compare list`}>
                        {savedCompare.compare.length}
                      </span>
                    ) : null}
                  </button>
                </li>
                {visibleMenu.map((category) => (
                  <li key={category.trigger}>
                    {/* FRIDGE-149 §3 — accordion trigger reports its state;
                        aria-controls only while its section is mounted. */}
                    <button
                      onClick={() => setExpandedMobileMenu(expandedMobileMenu === category.trigger ? null : category.trigger)}
                      aria-expanded={expandedMobileMenu === category.trigger}
                      aria-controls={expandedMobileMenu === category.trigger ? `mobile-menu-${category.trigger.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
                      className="w-full flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-100 font-medium"
                    >
                      {category.trigger}
                      <ChevronDown className={cn("h-5 w-5 text-gray-500 transition-transform", expandedMobileMenu === category.trigger && "rotate-180")} />
                    </button>
                    {expandedMobileMenu === category.trigger && (
                      <div
                        id={`mobile-menu-${category.trigger.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="ml-3 mt-1 border-l-2 border-[#0469f0] pl-3 space-y-3 pb-2"
                      >
                        {category.sections.map((section) => (
                          <div key={section.label}>
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#0469f0] py-1.5">
                              {section.label}
                            </div>
                            <ul className="space-y-0.5">
                              {section.items.map((item) => (
                                <li key={item.title}>
                                  <Link
                                    href={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="block py-2 px-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0469f0] rounded transition-colors"
                                  >
                                    {item.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
                {simpleLinks.map((link) => (
                  <li key={link.title}>
                    <Link
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-3 px-3 rounded-lg hover:bg-gray-100 font-medium"
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
          </div>
        </>
      )}

      <style jsx global>{`
        @keyframes slide-in {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.25s ease-out;
        }
      `}</style>
    </header>
  )
}
